import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, ChevronLeft, ChevronRight, Gift, Shield, Smartphone, Sparkles, Gem, DollarSign, ShoppingBag, MapPin } from 'lucide-react';
import { monthlyBenefits } from './constants';
import type { MembershipPlan, Redemption, Payment, DiscountPurchase } from './types';

// Map benefit types to Lucide icons
const benefitIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  vidrio_temperado: Shield,
  estuche: Smartphone,
  limpieza: Sparkles,
  hidrogel: Gem,
};

interface RedemptionCalendarProps {
  redemptions: Redemption[];
  plan: MembershipPlan;
  payments?: Payment[];
  discountPurchases?: DiscountPurchase[];
}

export const RedemptionCalendar: React.FC<RedemptionCalendarProps> = ({
  redemptions,
  plan,
  payments = [],
  discountPurchases = [],
}) => {
  const benefits = monthlyBenefits[plan];
  const today = new Date();
  const [viewDate, setViewDate] = useState(today);
  const [direction, setDirection] = useState(0);
  
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDay = firstDay.getDay();
  
  const monthName = viewDate.toLocaleDateString('es-CR', { month: 'long', year: 'numeric' });
  const isCurrentMonth = month === today.getMonth() && year === today.getFullYear();
  
  // Group redemptions by day
  const redemptionsByDay: { [key: number]: Redemption[] } = {};
  redemptions.forEach(r => {
    const date = new Date(r.created_at);
    if (date.getMonth() === month && date.getFullYear() === year) {
      const day = date.getDate();
      if (!redemptionsByDay[day]) redemptionsByDay[day] = [];
      redemptionsByDay[day].push(r);
    }
  });

  // Group payments by day
  const paymentsByDay: { [key: number]: Payment[] } = {};
  payments.forEach(p => {
    const date = new Date(p.created_at);
    if (date.getMonth() === month && date.getFullYear() === year) {
      const day = date.getDate();
      if (!paymentsByDay[day]) paymentsByDay[day] = [];
      paymentsByDay[day].push(p);
    }
  });

  // Group discount purchases by day
  const purchasesByDay: { [key: number]: DiscountPurchase[] } = {};
  discountPurchases.forEach(p => {
    const date = new Date(p.created_at);
    if (date.getMonth() === month && date.getFullYear() === year) {
      const day = date.getDate();
      if (!purchasesByDay[day]) purchasesByDay[day] = [];
      purchasesByDay[day].push(p);
    }
  });
  
  const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const navigateMonth = (dir: number) => {
    setDirection(dir);
    const newDate = new Date(year, month + dir, 1);
    setViewDate(newDate);
  };

  // Calculate activity intensity for heat map
  const getActivityIntensity = (count: number) => {
    if (count === 0) return '';
    if (count === 1) return 'bg-green-500/30 border-green-500/40';
    if (count === 2) return 'bg-green-500/50 border-green-500/60';
    return 'bg-green-500/70 border-green-500/80';
  };

  const renderCells = () => {
    const cells = [];
    
    for (let i = 0; i < startingDay; i++) {
      cells.push(<div key={`empty-${i}`} className="aspect-square" />);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dayRedemptions = redemptionsByDay[day];
      const dayPayments = paymentsByDay[day];
      const dayPurchases = purchasesByDay[day];
      const hasRedemption = dayRedemptions && dayRedemptions.length > 0;
      const hasPayment = dayPayments && dayPayments.length > 0;
      const hasPurchase = dayPurchases && dayPurchases.length > 0;
      const isToday = day === today.getDate() && isCurrentMonth;
      const redemptionCount = dayRedemptions?.length || 0;
      const purchaseCount = dayPurchases?.length || 0;
      
      cells.push(
        <motion.div
          key={day}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: day * 0.01 }}
          whileHover={{ scale: 1.1, zIndex: 10 }}
          className={`aspect-square rounded-xl flex flex-col items-center justify-center text-sm relative transition-all cursor-default group ${
            isToday 
              ? 'bg-gradient-to-br from-blue-500/40 to-blue-600/30 border-2 border-blue-400/60 shadow-lg shadow-blue-500/20' 
              : hasPurchase
                ? 'bg-purple-500/30 border border-purple-500/40'
                : hasRedemption 
                  ? `${getActivityIntensity(redemptionCount)} border`
                  : hasPayment
                    ? 'bg-emerald-500/20 border border-emerald-500/30'
                    : 'bg-white/5 hover:bg-white/10 border border-transparent'
          }`}
        >
          <span className={`font-semibold ${isToday ? 'text-blue-300' : hasPurchase ? 'text-purple-300' : hasRedemption ? 'text-green-300' : hasPayment ? 'text-emerald-300' : 'text-white/70'}`}>
            {day}
          </span>
          
          {/* Payment icon */}
          {hasPayment && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-1 -right-1"
            >
              <DollarSign className="w-4 h-4 text-emerald-400 drop-shadow-lg" />
            </motion.div>
          )}

          {/* Purchase icon */}
          {hasPurchase && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className={`absolute -top-1 ${hasPayment ? '-left-1' : '-right-1'}`}
            >
              <ShoppingBag className="w-4 h-4 text-purple-400 drop-shadow-lg" />
            </motion.div>
          )}
          
          {hasRedemption && (
            <div className="flex flex-col items-center gap-0.5 mt-0.5">
              {/* Desktop: larger icons */}
              <div className="hidden lg:flex gap-1">
                {dayRedemptions.slice(0, 2).map((r, i) => {
                  const IconComponent = benefitIconMap[r.benefit_type] || Gift;
                  return (
                    <motion.span 
                      key={i} 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <IconComponent className="w-4 h-4 text-green-400" />
                    </motion.span>
                  );
                })}
                {dayRedemptions.length > 2 && (
                  <span className="text-xs text-white/60 font-medium">+{dayRedemptions.length - 2}</span>
                )}
              </div>
              {/* Mobile: show count */}
              <span className="lg:hidden text-[10px] text-green-400 font-medium">
                {redemptionCount}
              </span>
            </div>
          )}

          {/* Purchase count on mobile when no redemptions */}
          {hasPurchase && !hasRedemption && (
            <span className="lg:hidden text-[10px] text-purple-400 font-medium mt-0.5">
              {purchaseCount}
            </span>
          )}
          
          {/* Tooltip on hover */}
          {(hasRedemption || hasPayment || hasPurchase) && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
              <div className="bg-slate-800 text-white text-xs px-3 py-2 rounded-lg shadow-xl border border-white/10 whitespace-nowrap min-w-[180px] max-w-[280px]">
                {/* Payment info */}
                {hasPayment && dayPayments.map((p, i) => (
                  <div key={`payment-${i}`} className="flex items-center gap-1.5 py-1 border-b border-white/10 last:border-b-0">
                    <DollarSign className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-emerald-400 font-medium">
                        {p.payment_type === 'activation' ? 'Activación' : 'Renovación'} - ₡{p.amount.toLocaleString()}
                      </div>
                      <div className="text-[10px] text-white/50">
                        {p.plan.toUpperCase()} • {p.billing_period}
                        {p.processor_name && ` • por ${p.processor_name}`}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Purchase info */}
                {hasPurchase && (
                  <>
                    <div className="font-semibold mb-2 mt-1 text-purple-400">{purchaseCount} compra{purchaseCount > 1 ? 's' : ''} con descuento</div>
                    {dayPurchases.slice(0, 3).map((p, i) => (
                      <div key={`purchase-${i}`} className="text-white/70 flex items-center gap-1.5 py-1 border-t border-white/5 first:border-t-0">
                        <ShoppingBag className="w-3 h-3 text-purple-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{p.product_name}</div>
                          <div className="text-[10px] text-green-400">Ahorro: ₡{p.savings.toLocaleString()}</div>
                          <div className="flex items-center gap-1 text-[10px] text-purple-400/70">
                            {p.registrar_name && <span>por {p.registrar_name}</span>}
                            {p.registrar_ubicacion && (
                              <span className="flex items-center gap-0.5 text-blue-400/70">
                                <MapPin className="w-2.5 h-2.5" />
                                {p.registrar_ubicacion}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {dayPurchases.length > 3 && (
                      <div className="text-[10px] text-white/40 mt-1">+{dayPurchases.length - 3} más</div>
                    )}
                  </>
                )}
                
                {/* Redemption info */}
                {hasRedemption && (
                  <>
                    <div className="font-semibold mb-2 mt-1">{redemptionCount} canje{redemptionCount > 1 ? 's' : ''}</div>
                    {dayRedemptions.slice(0, 3).map((r, i) => {
                      const IconComponent = benefitIconMap[r.benefit_type] || Gift;
                      return (
                        <div key={i} className="text-white/70 flex items-center gap-1.5 py-1 border-t border-white/5 first:border-t-0">
                          <IconComponent className="w-3 h-3 text-blue-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="truncate">{benefits[r.benefit_type as keyof typeof benefits]?.name || r.benefit_type}</div>
                            {r.redeemer_name && (
                              <div className="flex items-center gap-1 text-[10px] text-blue-400/70">
                                <span>por {r.redeemer_name}</span>
                                {r.redeemer_ubicacion && (
                                  <span className="flex items-center gap-0.5">
                                    <MapPin className="w-2.5 h-2.5" />
                                    {r.redeemer_ubicacion}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
              <div className="w-2 h-2 bg-slate-800 rotate-45 absolute left-1/2 -translate-x-1/2 -bottom-1 border-r border-b border-white/10" />
            </div>
          )}
        </motion.div>
      );
    }
    
    return cells;
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <motion.div
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
          >
            <Calendar className="w-6 h-6 text-blue-500" />
          </motion.div>
          <span className="text-white font-semibold text-lg">Calendario de Canjes</span>
        </div>
        
        {/* Month navigation */}
        <div className="flex items-center gap-2">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => navigateMonth(-1)}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ChevronLeft className="w-4 h-4 text-white" />
          </motion.button>
          <span className="text-white/80 min-w-[140px] text-center capitalize font-medium">
            {monthName}
          </span>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => navigateMonth(1)}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ChevronRight className="w-4 h-4 text-white" />
          </motion.button>
        </div>
      </div>
      
      {/* Days header */}
      <div className="grid grid-cols-7 gap-2 mb-3">
        {days.map(d => (
          <div key={d} className="text-center text-white/50 text-xs font-semibold py-2">
            {d}
          </div>
        ))}
      </div>
      
      {/* Calendar grid */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${month}-${year}`}
          initial={{ opacity: 0, x: direction * 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction * -50 }}
          transition={{ duration: 0.3 }}
          className="grid grid-cols-7 gap-2"
        >
          {renderCells()}
        </motion.div>
      </AnimatePresence>
      
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mt-5 pt-4 border-t border-white/10 text-xs text-white/60">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-gradient-to-br from-blue-500/40 to-blue-600/30 border-2 border-blue-400/60" />
          <span>Hoy</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500/30 border border-green-500/40" />
          <span>1 canje</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500/50 border border-green-500/60" />
          <span>2 canjes</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500/70 border border-green-500/80" />
          <span>3+ canjes</span>
        </div>
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-emerald-400" />
          <span>Pago</span>
        </div>
        <div className="flex items-center gap-2">
          <ShoppingBag className="w-4 h-4 text-purple-400" />
          <span>Compra con descuento</span>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Gift className="w-4 h-4 text-blue-400" />
          <span>Hover para detalles</span>
        </div>
      </div>
    </motion.div>
  );
};

export default RedemptionCalendar;
